import { Application } from 'express';
import { Logger } from '@unidev-hub/logger';
import { ConfigManager } from '@unidev-hub/config';
import swaggerJsdoc from 'swagger-jsdoc';
import swaggerUi from 'swagger-ui-express';
import path from 'path';
import { DocumentationOptions } from '../../types';
import { createPathResolver } from '../../utils';

/**
 * Setup ReDoc documentation
 *
 * @param app Express application
 * @param config Configuration manager
 * @param logger Logger instance
 * @param options Documentation options
 */
export async function setupRedoc(
  app: Application,
  config: ConfigManager,
  logger: Logger,
  options: DocumentationOptions = {}
): Promise<void> {
  try {
    // Get configuration for ReDoc
    const title = options.title || config.get<string>('api.docs.title', 'API Documentation');
    const version = options.version || config.get<string>('api.docs.version', '1.0.0');
    const description = options.description || config.get<string>('api.docs.description', 'API Documentation');
    const endpoint = options.endpoint || config.get<string>('api.docs.endpoint', '/api-docs');

    // Create path resolver for finding API files
    const pathResolver = createPathResolver(logger);

    // Determine API paths to scan
    let apiPaths = options.apiPaths || config.get<string[]>('api.docs.paths', []);

    // If no paths are specified, use default paths
    if (!apiPaths || apiPaths.length === 0) {
      const srcDir = pathResolver.resolveFromRoot('src');

      apiPaths = [
        path.join(srcDir, 'routes/**/*.ts'),
        path.join(srcDir, 'routes/**/*.js'),
        path.join(srcDir, 'models/**/*.ts'),
        path.join(srcDir, 'models/**/*.js'),
        path.join(srcDir, 'controllers/**/*.ts'),
        path.join(srcDir, 'controllers/**/*.js')
      ];
    }

    logger.debug('Generating ReDoc documentation', {
      title,
      version,
      endpoint,
      apiPaths
    });

    // Authentication configuration
    const authType = config.get<string>('auth.type', 'none');
    let securitySchemes: Record<string, any> = {
      bearerAuth: {
        type: 'http',
        scheme: 'bearer',
        bearerFormat: 'JWT',
        description: 'Enter your JWT token'
      }
    };

    // Add Azure AD security scheme if using Azure AD auth
    if (authType === 'azure') {
      const tenantId = config.get<string>('auth.azure.tenantId', '');
      const clientId = config.get<string>('auth.azure.clientId', '');
      const apiScope = config.get<string>('auth.azure.apiScope', 'api/.default');

      // Auth server URL
      const authServerUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0`;

      securitySchemes.azureAD = {
        type: 'oauth2',
        flows: {
          implicit: {
            authorizationUrl: `${authServerUrl}/authorize`,
            scopes: {
              [apiScope]: `Access to API`
            }
          }
        }
      };
    }

    // Create Swagger spec
    const swaggerOptions = {
      definition: {
        openapi: '3.0.0',
        info: {
          title,
          version,
          description
        },
        servers: [{
          url: config.get<string>('api.basePath', '/api'),
          description: 'API Server'
        }],
        components: {
          securitySchemes
        },
        security: [
          {
            bearerAuth: []
          }
        ]
      },
      apis: apiPaths
    };

    const swaggerSpec = swaggerJsdoc(swaggerOptions);

    // Add an endpoint to get the OpenAPI JSON
    app.get(`${endpoint}.json`, (req, res) => {
      res.setHeader('Content-Type', 'application/json');
      res.json(swaggerSpec);
    });

    // Setup Swagger UI using swagger-ui-express
    // This replaces the problematic ReDoc setup
    const swaggerUiOptions = {
      explorer: true,
      customCss: options.customCss || '.swagger-ui .topbar { display: none }',
      customSiteTitle: title
    };

    // Use swagger-ui-express correctly
    app.use(endpoint, swaggerUi.serve, swaggerUi.setup(swaggerSpec, swaggerUiOptions));

    logger.info(`API documentation available at ${endpoint}`);
    logger.debug(`OpenAPI JSON available at ${endpoint}.json`);

  } catch (error) {
    logger.error('Failed to setup API documentation', error as Error);
    throw error;
  }
}
